// Published review data only. The public widget API preserves Judge.me moderation,
// display names, verification, and product-group membership without copying reviews.
const sourceCache = new Map();
const pageSize = 6;

export function mergeReviews(groups) {
  const unique = new Map();
  groups.forEach(({ source, reviews }) => {
    reviews.forEach((review) => {
      if (!review.uuid || !Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5) {
        throw new Error('Invalid published review data');
      }
      if (!unique.has(review.uuid)) unique.set(review.uuid, { ...review, productKey: source.key, productLabel: source.label });
    });
  });
  return [...unique.values()];
}

export function reviewSummary(reviews) {
  const distribution = [0, 0, 0, 0, 0];
  reviews.forEach((review) => { distribution[review.rating - 1] += 1; });
  return {
    count: reviews.length,
    average: reviews.length ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length : 0,
    distribution
  };
}

export function selectReviews(reviews, { product = 'all', rating = 'all', sort = 'recent' } = {}) {
  return reviews.filter((review) => (product === 'all' || review.productKey === product) && (rating === 'all' || review.rating === Number(rating)))
    .sort((a, b) => {
      const ratingOrder = sort === 'highest' ? b.rating - a.rating : sort === 'lowest' ? a.rating - b.rating : 0;
      return ratingOrder || (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0) || a.uuid.localeCompare(b.uuid);
    });
}

async function loadSource(config, handle, request) {
  const cacheKey = `${config.shopDomain}:${handle}`;
  if (sourceCache.has(cacheKey)) return sourceCache.get(cacheKey);
  const pending = (async () => {
    const reviews = [];
    let totalPages = 1;
    for (let page = 1; page <= totalPages; page += 1) {
      const url = new URL('https://api.judge.me/api/v1/widgets/product_review');
      url.search = new URLSearchParams({ shop_domain: config.shopDomain, api_token: config.token, handle, page: String(page), per_page: '30', json_request: 'true' });
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      let payload;
      try {
        const response = await request(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Review service unavailable');
        payload = await response.json();
      } finally { clearTimeout(timeout); }
      if (!Array.isArray(payload.reviews) || !Number.isInteger(payload.total_pages) || payload.total_pages < 0 || payload.total_pages > 500) {
        throw new Error('Incomplete review response');
      }
      totalPages = payload.total_pages;
      if (totalPages && payload.current_page !== page) throw new Error('Unexpected review page');
      reviews.push(...payload.reviews);
    }
    return reviews;
  })();
  sourceCache.set(cacheKey, pending);
  try { return await pending; }
  catch (error) { sourceCache.delete(cacheKey); throw error; }
}

export async function loadReviews(config, request = fetch) {
  if (!config.token || !config.shopDomain || !config.sources?.length) throw new Error('Missing review configuration');
  const handles = [...new Set(config.sources.flatMap((source) => source.handles))];
  const loaded = new Map();
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(3, handles.length) }, async () => {
    while (next < handles.length) {
      const handle = handles[next++];
      loaded.set(handle, await loadSource(config, handle, request));
    }
  }));
  return mergeReviews(config.sources.map((source) => ({ source, reviews: source.handles.flatMap((handle) => loaded.get(handle)) })));
}

function element(tag, className, text) {
  const item = document.createElement(tag);
  if (className) item.className = className;
  if (text !== undefined) item.textContent = text;
  return item;
}

function stars(rating, label) {
  const root = element('span', 'pp-review-stars');
  root.setAttribute('role', 'img');
  root.setAttribute('aria-label', label);
  for (let index = 0; index < 5; index += 1) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'm12 2 3.1 6.3 7 .9-5.1 5 1.2 7-6.2-3.3L5.8 21l1.2-6.8-5-5 7-.9L12 2Z');
    path.setAttribute('fill', index < Math.round(rating) ? 'currentColor' : 'none');
    path.setAttribute('stroke', 'currentColor');
    svg.append(path);
    root.append(svg);
  }
  return root;
}

function safeMediaUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.href : null;
  } catch { return null; }
}

function initialize(root) {
  if (root.dataset.reviewReady) return;
  root.dataset.reviewReady = 'true';
  const sources = JSON.parse(root.querySelector('[data-review-sources]').textContent);
  const labels = JSON.parse(root.querySelector('[data-review-strings]').textContent);
  const text = (key, values = {}) => {
    if (key === 'stars' && Number(values.rating) === 1) key = 'one_star';
    if (key === 'showing' && Number(values.count) === 1) key = 'showing_one';
    return Object.entries(values).reduce((result, [name, value]) => result.replaceAll(`__${name}__`, String(value)), labels[key] || key);
  };
  const status = root.querySelector('[data-review-status]');
  const content = root.querySelector('[data-review-content]');
  const retry = root.querySelector('[data-review-retry]');
  const list = root.querySelector('[data-review-list]');
  const productFilter = root.querySelector('[data-review-product]');
  const ratingFilter = root.querySelector('[data-review-rating]');
  const sortFilter = root.querySelector('[data-review-sort]');
  const pageSelect = root.querySelector('[data-review-page]');
  const previous = root.querySelector('[data-review-previous]');
  const next = root.querySelector('[data-review-next]');
  let reviews = [];
  let currentPage = 1;

  const renderReview = (review) => {
    const article = element('article', 'pp-published-review');
    article.dataset.reviewId = review.uuid;
    article.dataset.reviewProduct = review.productKey;
    article.dataset.reviewRating = String(review.rating);
    const meta = element('div', 'pp-published-review__meta');
    meta.append(stars(review.rating, text('stars', { rating: review.rating })));
    const date = new Date(review.created_at);
    if (Number.isFinite(date.getTime())) {
      const time = element('time', '', new Intl.DateTimeFormat(document.documentElement.lang || 'en', { year: 'numeric', month: 'short', day: 'numeric' }).format(date));
      time.dateTime = date.toISOString();
      meta.append(time);
    }
    article.append(meta);
    const author = element('div', 'pp-published-review__author');
    author.append(element('strong', '', review.is_anonymous_reviewer ? text('anonymous') : review.reviewer_name || text('anonymous')));
    if (review.verified_buyer === true) author.append(element('span', 'pp-review-verified', text('verified')));
    article.append(author, element('p', 'pp-published-review__product', text('review_for', { product: review.productLabel })));
    if (review.title) article.append(element('h3', '', review.title));
    article.append(element('p', 'pp-published-review__body', review.body || ''));
    if (review.pictures_urls?.length) {
      const pictures = element('div', 'pp-published-review__pictures');
      review.pictures_urls.forEach((picture) => {
        const original = safeMediaUrl(picture.original);
        const thumbnail = safeMediaUrl(picture.small || picture.compact || picture.original);
        if (!original || !thumbnail) return;
        const link = element('a');
        link.href = original;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        const image = element('img');
        image.src = thumbnail;
        image.alt = text('photo', { product: review.productLabel });
        image.loading = 'lazy';
        image.width = 80;
        image.height = 80;
        link.append(image);
        pictures.append(link);
      });
      article.append(pictures);
    }
    if (review.reply_content) {
      const reply = element('div', 'pp-published-review__reply');
      reply.append(element('strong', '', text('reply')), element('p', '', review.reply_content));
      article.append(reply);
    }
    return article;
  };

  const render = (moveFocus = false) => {
    const filtered = selectReviews(reviews, { product: productFilter.value, rating: ratingFilter.value, sort: sortFilter.value });
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    currentPage = Math.min(currentPage, pages);
    const start = (currentPage - 1) * pageSize;
    list.replaceChildren(...filtered.slice(start, start + pageSize).map(renderReview));
    root.querySelector('[data-review-results]').textContent = filtered.length ? text('showing', { first: start + 1, last: Math.min(start + pageSize, filtered.length), count: filtered.length }) : text('no_matches');
    pageSelect.replaceChildren(...Array.from({ length: pages }, (_, index) => {
      const option = element('option', '', text('page_of', { page: index + 1, pages }));
      option.value = String(index + 1);
      return option;
    }));
    pageSelect.value = String(currentPage);
    previous.disabled = currentPage === 1;
    next.disabled = currentPage === pages;
    root.querySelector('[data-review-pagination]').hidden = pages <= 1;
    root.querySelectorAll('[data-rating-button]').forEach((button) => { button.setAttribute('aria-pressed', String(button.dataset.ratingButton === ratingFilter.value)); });
    if (moveFocus) {
      list.focus({ preventScroll: true });
      root.querySelector('.pp-review-filters').scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  };

  const renderSummary = () => {
    const summary = reviewSummary(reviews);
    const average = summary.average.toFixed(1);
    const rootAverage = root.querySelector('[data-review-average]');
    rootAverage.replaceChildren(element('strong', '', average), stars(summary.average, text('out_of_five', { rating: average })), element('span', '', text('count', { count: summary.count })));
    const distribution = root.querySelector('[data-review-distribution]');
    distribution.replaceChildren(...[5, 4, 3, 2, 1].map((rating) => {
      const count = summary.distribution[rating - 1];
      const button = element('button', 'pp-review-rating-row');
      button.type = 'button';
      button.dataset.ratingButton = String(rating);
      button.setAttribute('aria-label', text('filter_stars', { rating, count }));
      button.append(element('span', '', text('stars', { rating })));
      const bar = element('span', 'pp-review-rating-row__track');
      bar.setAttribute('aria-hidden', 'true');
      const fill = element('span');
      fill.style.width = `${count / summary.count * 100}%`;
      bar.append(fill);
      button.append(bar, element('span', 'pp-review-rating-row__count', String(count)));
      button.addEventListener('click', () => { ratingFilter.value = ratingFilter.value === String(rating) ? 'all' : String(rating); currentPage = 1; render(); });
      return button;
    }));
    const badge = document.querySelector(`[data-review-summary="${CSS.escape(root.dataset.instanceId)}"]`);
    if (badge) {
      badge.replaceChildren(stars(summary.average, text('out_of_five', { rating: average })), element('strong', '', average), element('span', '', text('count', { count: summary.count })));
      badge.hidden = false;
    }
  };

  const load = async () => {
    status.hidden = false;
    status.textContent = text('loading');
    retry.hidden = true;
    root.setAttribute('aria-busy', 'true');
    try {
      reviews = await loadReviews({ shopDomain: root.dataset.shopDomain, token: root.dataset.publicToken, sources });
      if (!reviews.length) { status.textContent = text('empty'); return; }
      while (productFilter.options.length > 1) productFilter.remove(1);
      const sourceCounts = sources.map((source) => ({ ...source, count: reviews.filter((review) => review.productKey === source.key).length })).filter((source) => source.count > 0);
      sourceCounts.forEach((source) => {
        const option = element('option', '', `${source.label} (${source.count})`);
        option.value = source.key;
        productFilter.append(option);
      });
      const sourceSummary = root.querySelector('[data-review-source-summary]');
      if (sourceSummary) {
        sourceSummary.textContent = sourceCounts.map((source) => `${source.label} (${source.count})`).join(' · ');
        sourceSummary.hidden = false;
      }
      renderSummary();
      render();
      content.hidden = false;
      status.hidden = true;
    } catch {
      status.textContent = text('error');
      retry.hidden = false;
    } finally { root.setAttribute('aria-busy', 'false'); }
  };
  [productFilter, ratingFilter, sortFilter].forEach((filter) => filter.addEventListener('change', () => { currentPage = 1; render(); }));
  previous.addEventListener('click', () => { currentPage -= 1; render(true); });
  next.addEventListener('click', () => { currentPage += 1; render(true); });
  pageSelect.addEventListener('change', () => { currentPage = Number(pageSelect.value); render(true); });
  retry.addEventListener('click', load);
  load();
}

function initializeAll() { document.querySelectorAll('[data-bundle-review-feed]').forEach(initialize); }
if (typeof document !== 'undefined') {
  initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
}
